import React, { memo, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useAppSelector } from "redux/hooks";
import { selectEmail } from "redux/authSlice";
import { SignRequest } from "models/interfaces";
import { RootState } from "utils/store";
import { isTruthy } from "helpers/methods";
import {
  BrandingSettings,
  fetchBrandingSettings,
  toBrandingSettings,
} from "screens/Settings/Branding/BrandingService";
import { getRenderableLogoUrl } from "screens/Settings/Branding/component/LogoUpload";
import emailConfigurationsStyles from "../EmailConfigurations.styles";
import emailConfigurationsTokens from "../EmailConfigurations.tokens";
import { MailPreviewGlyph } from "./EmailConfigIcons";
import {
  getEmbedEmailBranding,
  isEmbedEditorMode,
} from "embed/EmbedEditorMode";

const styles = emailConfigurationsStyles;
const { preview, palette } = emailConfigurationsTokens;

interface EmailPreviewProps {
  signRequest: SignRequest;
  /**
   * Accepted for call-site compatibility with the mobile flow; the panel is
   * fluid, so it needs no width branch of its own.
   */
  isMobile?: boolean;
}

/** One `From: / To: / CC: / Subject:` line in the preview's meta block. */
const MetaRow = memo<{ label: string; value: string; truncate?: boolean }>(
  ({ label, value, truncate }) => (
    <Typography component="div" sx={styles.previewMetaRow(truncate)}>
      <Box component="span" sx={styles.previewMetaLabel}>
        {label}
      </Box>{" "}
      {value}
    </Typography>
  ),
);
MetaRow.displayName = "MetaRow";

/**
 * Sticky "Email preview" panel beside the settings accordions — a live render of
 * the mail the recipients receive, drawn from `Email Configurations.dc.html`.
 *
 * Workspace branding still overrides the sender, logo, CTA colour and footer
 * text so the requester sees what the recipient will actually get.
 */
const EmailPreviewComponent = memo<EmailPreviewProps>(({ signRequest }) => {
  const userEmail = useAppSelector(selectEmail);
  const { userName } = useAppSelector((state: RootState) => state.auth);

  const [branding, setBranding] = useState<BrandingSettings | null>(null);
  useEffect(() => {
    // The embedded wizard has no login to read the branding with; the editor bundle carries the branding
    // the signers' emails will use instead.
    if (isEmbedEditorMode()) {
      const embedded = getEmbedEmailBranding();
      setBranding(embedded ? toBrandingSettings(embedded) : null);
      return;
    }
    let cancelled = false;
    fetchBrandingSettings()
      .then((data) => {
        if (!cancelled) setBranding(data);
      })
      .catch(() => {
        if (!cancelled) setBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isBrandingActive = !!branding?.enabled;
  const brandName = (isBrandingActive && branding?.name) || "Inkfree";
  const logoUrl = (isBrandingActive && branding?.logoUrl) || "";
  const primaryColor =
    (isBrandingActive && branding?.primaryColor) || palette.accent;
  const buttonTextColor =
    (isBrandingActive && branding?.buttonTextColor) || palette.white;
  const fromEmail = (isBrandingActive && branding?.senderEmail) || userEmail;
  const fromName = (isBrandingActive && branding?.senderName) || userName || "";
  const copyright = `© ${new Date().getFullYear()} ${brandName}. All Rights Reserved.`;
  const footerRight =
    (isBrandingActive && branding?.footerText?.trim()) || copyright;

  const signers = signRequest.signers ?? [];
  const recipients = signers
    .map((s) => (!s.cc ? s.email : undefined))
    .filter(Boolean)
    .join(", ");
  const cc = signers
    .map((s) => (s.cc ? s.email : undefined))
    .filter(Boolean)
    .join(", ");

  const message = signRequest.emailMessage?.trim() ?? "";

  return (
    <Box sx={styles.previewSticky}>
      <Box sx={styles.previewCard}>
        <Box sx={styles.previewHead}>
          <MailPreviewGlyph
            sx={{ width: preview.headIconSize, height: preview.headIconSize }}
          />
          <Typography component="span" sx={styles.previewHeadTitle}>
            Email preview
          </Typography>
          <Box component="span" sx={styles.previewLive}>
            Live
          </Box>
        </Box>

        <Box sx={styles.previewMeta}>
          <MetaRow
            label="From:"
            value={fromName ? `${fromName} <${fromEmail}>` : fromEmail}
          />
          <MetaRow label="To:" value={recipients} />
          {isTruthy(cc) && <MetaRow label="CC:" value={cc} />}
          <MetaRow
            label="Subject:"
            value={signRequest.emailSubject || "(no subject)"}
            truncate
          />
        </Box>

        <Box sx={styles.previewBody}>
          <Typography component="div" sx={styles.previewGreeting}>
            Hi [Signer],
          </Typography>

          <Typography component="div" sx={styles.previewPara}>
            <Box component="span" sx={styles.previewParaStrong}>
              {fromName || "[Sender]"}
            </Box>{" "}
            has requested your signature on Documents. To review and sign the
            document, please click the button below:
          </Typography>

          <Box sx={styles.previewCta(primaryColor, buttonTextColor)}>
            Review and Sign
          </Box>

          <Typography component="div" sx={styles.previewNote}>
            You have until [expiration date]* to sign this document. This
            communication contains a secure link — do not share it with
            others. If you're unwilling to sign, you can either delegate the
            request or decline it.
          </Typography>

          {!!message && (
            <Typography
              component="div"
              sx={{ ...styles.previewPara, wordBreak: "break-word" }}
            >
              {message}
            </Typography>
          )}

          <Typography component="div" sx={styles.previewNote}>
            Having trouble accessing the link? Paste the following URL into
            your browser:
            <br />
            [URL]
            <br />
            *All dates are set to EST.
          </Typography>

          <Typography component="div" sx={styles.previewSignOff}>
            Have a wonderful day!
            <br />
            <Box component="span" sx={styles.previewTeam(primaryColor)}>
              The {brandName} Team
            </Box>
          </Typography>
        </Box>

        <Box sx={styles.previewFooter}>
          <Box sx={styles.previewBrand}>
            {logoUrl ? (
              <Box
                component="img"
                src={getRenderableLogoUrl(logoUrl)}
                alt={brandName}
                title={brandName}
                sx={styles.previewLogo}
              />
            ) : (
              <>
                <Box component="span" sx={styles.previewBrandBadge(primaryColor)}>
                  {brandName}
                </Box>
                <Box component="span" sx={styles.previewEsign}>
                  e-Sign
                </Box>
              </>
            )}
          </Box>
          <Typography component="span" sx={styles.previewCopyright}>
            {footerRight}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

EmailPreviewComponent.displayName = "EmailPreviewComponent";

export default EmailPreviewComponent;
